import React, { useEffect, useMemo, useState } from "react";
import { DraftReferenceState } from "../../api/referenceOptions";
import { useKeycloakToken } from '../common/useKeycloak';
import { useNavigate, useParams } from 'react-router-dom';
import { Route, NewIntegration, Organization, ProviderType, buildProviderType, buildNewIntegration } from "../common/ObjectTypes";
import { createNewRoute } from "../../api/routes/routesApi";
import NotFoundPage from "../common/NotFoundPage";
import { filterDestinationOwner } from "../destinations/DestinationHelperFunctions";
import { toast } from "react-toastify";
import { loadOrganizations } from "../../api/organizations/loadOrganizations";
import { page_size } from "../../api/apiUtils";
import { useTranslation } from "react-i18next";
import NewRouteStepTwo from "./NewRouteStepTwo";
import { loadProviderTypes } from "../../api/routes/loadProviderTypesNewRoute";
import { useKeycloak } from "@react-keycloak/web";
import RouteProgressIndicator from "./NewRouteProgressIndicator/RouteProgressIndicator";
import { trackRouteCreated, trackRouteCreationAbandoned } from '../../analytics/analytics';
import { loadCurrentUserDetails } from "../organizations/loadCurrentUserDetails";
import { providerNeedsBaseUrl } from "./providerBaseUrl";
import { ActionConfigErrors, validateActionConfigs } from "../common/SchemaFormTemplates/validateActionConfigs";
import { routeSetupActions } from "../common/SchemaFormTemplates/actionFilters";
import { actionIsSentOnCreate } from "../common/configPanel/actionSetup";
import { configurationsForCreate } from "../common/SchemaFormTemplates/validateActionConfigs";
import { routeRecordId } from "./routeDisplayName";
import { validateBaseUrl } from "../destinations/validateBaseUrl";
import { constructEarthRangerUrl, isEarthRangerDestinationType } from "../destinations/earthRangerUrl";
import { buildDraftReference } from "../common/buildDraftReference";
import { buttonClasses, skeletonClasses, wizardClasses } from "../common/tailwindClassnames";
import { getRouteKind } from "../common/routeKind";

export function ManageAddRouteStepTwo() {
    type FilterTypes = {
        endpoint__in: FilterTags[],
        enabled: FilterTags[],
        type__in: FilterTags[],
        owner__in: FilterTags[],
        ordering: string,
        search: string
    };
    function createFilterTypes(
        endpoint__in: FilterTags[],
        enabled: FilterTags[],
        type__in: FilterTags[],
        owner__in: FilterTags[],
        ordering: string,
        search: string
    ): FilterTypes {
        return {
            endpoint__in: endpoint__in || [],
            enabled: enabled || [],
            type__in: type__in || [],
            owner__in: owner__in || [],
            ordering: ordering || "",
            search: search || ""
        }
    }
    type FilterTags = {
        name: string,
        id: string
    };
    const { t } = useTranslation();
    const { pt_value } = useParams();
    const [token, showLoginModal, setShowLoginModal] = useKeycloakToken();
    const { keycloak, initialized } = useKeycloak();
    const [currentMemberIsAdmin, setCurrentMemberIsAdmin] = useState<boolean>(false);
    const [destinationFilter, setDestinationFilter] = useState<FilterTypes>(createFilterTypes([], [], [], [], "", ""));
    const [initialRoute, setInitialRoute] = useState<NewIntegration>(buildNewIntegration(true))
    const [createdRoute, setCreatedRoute] = useState<Route>()
    const [updatedConfigurations, setUpdatedConfigurations] = useState<any[]>([]);
    const [errors, setErrors] = useState({});
    // Keyed by action id: { [action_id]: { [field]: message } }.
    const [rsjfErrors, setRsjfErrors] = useState<ActionConfigErrors>({});
    const [routeId, setRouteId] = useState("");

    const [reloadDestinations, setReloadDestinations] = useState(false);

    const [providerTypes, setProviderTypes] = useState<ProviderType[]>([]);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loadingOrganizations, setLoadingOrganizations] = useState(false);
    const [loadMoreOrganizations, setLoadMoreOrganizations] = useState(true);
    const [organizationOffset, setOrganizationOffset] = useState<number>(0);
    const [orgOffset, setOrgOffset] = useState<number>(0);

    const [selectedProviderType, setSelectedProviderType] = useState<ProviderType>(buildProviderType());
    const [pageLoaded, setPageLoaded] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [currentStep, setCurrentStep] = useState(2);
    const navigate = useNavigate();

    const draftReference: DraftReferenceState | undefined = useMemo(() => buildDraftReference({
        type: selectedProviderType,
        owner: initialRoute.owner,
        baseUrl: initialRoute.base_url,
        updatedConfigurations,
    }), [selectedProviderType, initialRoute.owner, initialRoute.base_url, updatedConfigurations]);

    const loadOrganizationsData = async (offset: number, search_str: string) => {
        // Check if user is authenticated before making API calls
        const isLoggedIn = keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null;
        if (!isLoggedIn) {
            return;
        }

        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            try {
                const [orgs, total_orgs, error] = await loadOrganizations(token, offset, search_str, organizations);
                setLoadingOrganizations(true);
                if (!error) {
                    setOrganizations(orgs);
                    if (offset + page_size >= total_orgs) {
                        setLoadMoreOrganizations(false);
                    }
                    setLoadingOrganizations(false);
                }
            } catch (err) {
                // Silently handle errors - they're already handled by the interceptor
                console.log('Failed to load organizations:', err);
                setLoadingOrganizations(false);
            }
        }
    }

    const loadProviderTypesData = async (search_str: string) => {
        // Check if user is authenticated before making API calls
        const isLoggedIn = keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null;
        if (!isLoggedIn) {
            return;
        }

        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            try {
                const [provider_types, error] = await loadProviderTypes(token, search_str);
                if (!error && provider_types && provider_types.length > 0 && provider_types[0]) {
                    setNotFound(false);
                    setSelectedProviderType(provider_types[0]);
                    setInitialRoute((prevRoute: any) => ({
                        ...prevRoute,
                        type: provider_types[0].id
                    }))
                    setPageLoaded(true);
                } else if (!error) {
                    // The provider-type search returned nothing — the :pt_value in the
                    // URL doesn't match any provider (e.g. a stale bookmark)
                    setNotFound(true);
                }
            } catch (err) {
                // Silently handle errors - they're already handled by the interceptor
                console.log('Failed to load provider types:', err);
            }
        }
    }

    useEffect(() => {
        pt_value && loadProviderTypesData(pt_value);
        loadOrganizationsData(organizationOffset, "");

    }, [token, reloadDestinations, providerTypes])


    const handleChangeEndpoint = (e: any) => {
        setInitialRoute((prevRoute: any) => ({
            ...prevRoute,
            base_url: e.target.value
        }))
    }

    const handleChangeSchemaForm = (e: any, action_id: string) => {
        if (!e) return;
        const newConfig = { "action": action_id, "data": e };
        // One array, built once and used for both states. A functional update on one
        // and a closure read of stale updatedConfigurations on the other drops a
        // section edited exactly once from the POST, and mutating in place hands
        // React the same reference so nothing re-renders. Same fix as GUNDI-5660
        // on ManageDestinationModal.
        const index_to_find = updatedConfigurations.findIndex((a: any) => a.action === action_id);
        const next = index_to_find === -1
            ? [...updatedConfigurations, newConfig]
            : updatedConfigurations.map((c: any, i: number) => (i === index_to_find ? newConfig : c));
        setUpdatedConfigurations(next);
        setInitialRoute((prevRoute: any) => ({
            ...prevRoute,
            configurations: next,
        }));
    }

    const handleChangeName = (e: any) => {
        setInitialRoute((prevRoute: any) => ({
            ...prevRoute,
            name: e.target.value
        }))
    }

    const handleSelectOwner = async (e: any) => {
        // Check if e and e.value are defined
        if (!e || !e.value) {
            return;
        }

        // Check if user is authenticated before making API calls
        const isLoggedIn = keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null;
        if (!isLoggedIn) {
            return;
        }

        try {
            const isAdmin = await loadCurrentUserDetails(e.value, keycloak.token ? keycloak.token : "");
            setCurrentMemberIsAdmin(isAdmin);
            setInitialRoute((prevRoute: any) => ({
                ...prevRoute,
                owner: e.value
            }))
            setDestinationFilter(filterDestinationOwner({
                target: {
                    "name": e.label || '',
                    "value": e.value,
                }
            }, destinationFilter));
            setReloadDestinations(true);
        } catch (err) {
            // Silently handle errors - they're already handled by the interceptor
            console.log('Failed to load user details:', err);
        }
    }

    // The sections whose required fields count: everything the screen shows, minus
    // anything switched off. Same predicate the payload is built from, so the
    // screen validates exactly what it sends.
    const enabledSetupActions = () =>
        routeSetupActions(selectedProviderType?.actions).filter((action: any) =>
            actionIsSentOnCreate(action, updatedConfigurations.find((c: any) => c.action === action.id)?.data));

    const formIsValid = async () => {
        let form_errors: any = {};
        if (initialRoute.name.length === 0) form_errors.name = t("Name is required.")
        if (initialRoute.name.length > 50) form_errors.name = t("Name cannot exceed 50 characters.");
        // Gundi cannot reach these technologies without an address, so the
        // Route can't be created without one.
        const needsBaseUrl = providerNeedsBaseUrl(selectedProviderType?.value);
        const isEarthRanger = isEarthRangerDestinationType(selectedProviderType?.name || "");
        // Shared validator: this copy used to be the untranslated one, and said
        // "url" where the other screens said "URL".
        const urlError = validateBaseUrl({
            value: initialRoute.base_url, asSiteName: isEarthRanger, optional: !needsBaseUrl, t,
        });
        if (urlError) form_errors.base_url = urlError;
        if (initialRoute.owner === "") form_errors.owner = t("Select a workspace.")

        setErrors(form_errors)

        // Every section the screen shows, not just Credentials: they are all on
        // the page, so an empty required field is visible work the user owes. Left
        // unvalidated, it reaches the API as a defaults-only configuration and the
        // whole Route is rejected with no field to point at.
        // A section switched off by its own toggle is inert — the connector never
        // runs it — so its required fields are not work the user owes.
        const rsjf_errors = validateActionConfigs(
            enabledSetupActions(),
            updatedConfigurations,
            t,
        );
        setRsjfErrors({ ...rsjf_errors });
        return Object.keys(form_errors).length === 0 && Object.keys(rsjf_errors).length === 0;
    }

    const handleCreateRoute = async (routeToAdd: NewIntegration) => {
        if (!(await formIsValid())) {
            // The blocking fields can sit in a section the user never opened, and
            // nothing relocates them — same toast as the New Connection screen.
            toast.error(t("There are fields that require your attention. Please check and correct the errors."),
                { position: toast.POSITION.TOP_RIGHT });
            return;
        }

        // Check if user is authenticated before making API calls
        const isLoggedIn = keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null;
        if (!isLoggedIn) {
            return;
        }

        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            try {
                // File-widget values go to the backend as JSON strings; done here,
                // on the payload, rather than by rewriting the form's state.
                // Switched-off sections are left out — see configurationsForCreate.
                const temp = await createNewRoute(token, {
                    ...routeToAdd,
                    configurations: configurationsForCreate(
                        selectedProviderType?.actions,
                        routeToAdd.configurations ?? [],
                    ),
                });
                // The record can arrive as default_route or, on some responses, only
                // in routing_rules — routeRecordId knows both. Bailing on the
                // narrower check left a created Route with no toast and no
                // navigation, so the user pressed Create again and made a duplicate.
                const createdRecordId = temp ? routeRecordId(temp) : undefined;
                if (!temp || !createdRecordId) {
                    toast.error(`${t("Something went wrong. Please try again or contact support.")}`, {
                        position: toast.POSITION.TOP_RIGHT
                    });
                    return;
                }
                setCreatedRoute(temp)
                setRouteId(createdRecordId);
                const workspaceName = temp.owner?.name || organizations.find((o: Organization) => o.id === initialRoute.owner)?.name;
                trackRouteCreated(temp.id, selectedProviderType?.name, workspaceName);
                toast.success(`${t("New Route made, ready to add destinations")}`, {
                    position: toast.POSITION.TOP_RIGHT
                });
                // Destinations are added on the flow map now — land there with the
                // "+ Add destination" button highlighted instead of a third step.
                // `temp.id` is the provider integration the POST created, which is
                // what route URLs address; createdRecordId above still gates on the
                // route record existing, since without one there is no Route page.
                navigate(`/routes/${temp.id}`, {
                    state: { highlightAddDestination: true }
                })
            } catch (err: any) {
                console.log('Failed to create route:', err);
                // Session expiry is handled by the axios interceptor + LoginModal; don't stack a toast on it.
                if (err?.response?.status === 401) return;
                toast.error(t("Something went wrong. Please try again or contact support."), {
                    position: toast.POSITION.TOP_RIGHT
                });
            }
        }
    }

    const handleClickNext = async (newStep: number) => {
        if (newStep === 3) {
            if (!(await formIsValid())) {
                setCurrentStep(currentStep);
                return;
            }
            // On success this navigates to the Route's flow map — the wizard
            // ends here, so there is no step 3 to advance to.
            // EarthRanger sites are typed as a site name; the record stores the URL.
            await handleCreateRoute(isEarthRangerDestinationType(selectedProviderType?.name || "")
                ? { ...initialRoute, base_url: constructEarthRangerUrl(initialRoute.base_url) }
                : initialRoute);
        }
    }

    function handleSearchOrgDropdown(e: any) {
        if (e.length > 1) loadOrganizationsData(orgOffset, e);
    }

    const handleLoadMoreOrganizations = () => {
        setOrganizationOffset(organizationOffset + page_size);
        loadOrganizationsData(organizationOffset + page_size, "");
    }

    const handleCancel = () => {
        const workspaceName = organizations.find((o: Organization) => o.id === initialRoute.owner)?.name;
        trackRouteCreationAbandoned(2, selectedProviderType?.name, workspaceName);
        navigate("/")
    }

    if (notFound) return <NotFoundPage />;

    return !pageLoaded ?
        (
            <div>
                <div className={wizardClasses.band}>
                    <div className={wizardClasses.row}>
                        <h1 className={wizardClasses.title}>{t("New")} {t("Route")}</h1>
                    </div>
                </div>
                <div className={wizardClasses.body}>
                <div className={wizardClasses.row}>
                    {/* The provider type hasn't loaded yet, but the webhook kind is
                        derivable from the URL slug — avoids a 4-to-5-step layout shift */}
                    <RouteProgressIndicator
                        stepZeroStatus="completed"
                        stepOneStatus="in-progress"
                        stepTwoStatus="upcoming"
                        webhookStepStatus={pt_value === "generic_webhooks" ? "upcoming" : undefined}
                    />
                    <div className="mt-8 w-full rounded p-[16px]">
                        <div className={`h-5 w-96 max-w-full ${skeletonClasses.base}`} />
                        <div className="mt-6 flex flex-row">
                            <div className="flex flex-col w-1/2 mr-3 gap-2">
                                <div className={`h-4 w-32 ${skeletonClasses.base}`} />
                                <div className={`h-10 w-full ${skeletonClasses.base}`} />
                            </div>
                            <div className="flex flex-col w-1/2 mr-3 gap-2">
                                <div className={`h-4 w-32 ${skeletonClasses.base}`} />
                                <div className={`h-10 w-full ${skeletonClasses.base}`} />
                            </div>
                        </div>
                        <div className="mt-8 flex flex-col gap-2">
                            <div className={`h-5 w-80 max-w-full ${skeletonClasses.base}`} />
                            <div className={`h-10 w-full ${skeletonClasses.base}`} />
                            <div className={`h-10 w-full ${skeletonClasses.base}`} />
                        </div>
                    </div>
                </div>
                </div>
            </div>
        ) : (
            <div>
                <div className={wizardClasses.band}>
                    <div className={wizardClasses.row}>
                        <h1 className={wizardClasses.title}>{t("New")} {selectedProviderType?.name || ''} {t("Route")}</h1>
                    </div>
                </div>
                <div className={wizardClasses.body}>
                <div className={wizardClasses.row}>
                    <RouteProgressIndicator
                        stepZeroStatus="completed"
                        stepOneStatus="in-progress"
                        stepTwoStatus="upcoming"
                        webhookStepStatus={getRouteKind(selectedProviderType) === 'webhook' ? "upcoming" : undefined}
                    />
                    <NewRouteStepTwo
                        currentMemberIsAdmin={currentMemberIsAdmin}
                        initialRoute={initialRoute}
                        errors={errors}
                        rsjfErrors={rsjfErrors}
                        onChangeSchemaForm={handleChangeSchemaForm}
                        configurations={updatedConfigurations}
                        onChangeName={handleChangeName}
                        onChangeEndpoint={handleChangeEndpoint}
                        onSelectOwner={handleSelectOwner}
                        selectedProviderType={selectedProviderType}
                        organizations={organizations}
                        loadMoreOrgs={handleLoadMoreOrganizations}
                        orgInputChange={handleSearchOrgDropdown}
                        draftReference={draftReference}
                        authToken={typeof token === 'string' ? token : undefined}
                    />
                </div>
                </div>

                <div className={wizardClasses.footer}>
                    <div className="px-4 md:px-8 desktop:px-[60px]">
                        <div className={`${wizardClasses.row} flex flex-row items-center gap-3`}>
                        <button className={buttonClasses.primary}
                            onClick={() => handleClickNext(currentStep + 1)}
                            disabled={!initialRoute.name.trim() || !initialRoute.owner}
                            data-testid="ManageAddRouteStepTwo-next-button"
                        >
                            {t("Create Route")}
                        </button>
                        <button className={buttonClasses.cancel}
                            type="button"
                            onClick={handleCancel}
                            data-testid="ManageAddRouteStepTwo-cancel-button"
                        >
                            {t("Cancel")}
                        </button>
                        </div>
                    </div>
                </div>
            </div>
        )
}